import { Modal } from "@general-backoffice/core/index";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Alert, Button, Col, Progress, Row, UncontrolledTooltip } from "reactstrap";

import equitiLogo from "../../media/brokers/equiti.png";
import fbsLogo from "../../media/brokers/fbs.svg";
import pepperstoneLogo from "../../media/brokers/pepperstone.svg";
import squaredLogo from "../../media/brokers/squared.png";
import vantageLogo from "../../media/brokers/vantage.svg";
import xmLogo from "../../media/brokers/XMLogo.svg";
import useModal from "../hooks/useModal";

type UserType = "individual" | "company";
type CapitalTier = "under2k" | "2k-20k" | "over20k" | "under20k";
type TFunction = (key: string, defaultValueOrOptions?: string | Record<string, unknown>) => string;

interface Broker {
  id: string;
  name: string;
  minDeposit: number | Record<string, number>;
  userTypes: string[];
  logo: string;
  logoWidth: number;
  url: string;
  buttonStyle: React.CSSProperties;
  hasCopyLink?: boolean;
  hasDetailedInstructions?: boolean;
}

export const useClientOnboardingModal = ({ autoOpen }: { autoOpen?: boolean }) =>
  useModal({ Modal: ClientOnboardingModal, autoOpen });

// Broker configuration
// minDeposit is per userType - for brokers serving both, use object { individual: X, company: Y }
const BROKERS = {
  xm: {
    id: "xm",
    name: "XM",
    minDeposit: 2000, // Only for individuals, Micro accounts
    userTypes: ["individual"], // XM does not offer company accounts
    logo: xmLogo,
    logoWidth: 80,
    url: "https://clicks.pipaffiliates.com/c?m=120014&c=573464",
    buttonStyle: { backgroundColor: "#1a1a1a", color: "white" },
    hasCopyLink: true,
    hasDetailedInstructions: true,
  },
  equiti: {
    id: "equiti",
    name: "Equiti",
    minDeposit: 20000, // For individuals (Standard accounts)
    userTypes: ["individual"],
    logo: equitiLogo,
    logoWidth: 100,
    url: "https://portal.my-equiti.com/sc/register/?accountType=Standard&clickid=498083&affid=C01620815",
    buttonStyle: { border: "1px solid #00afab", color: "#00afab", backgroundColor: "white" },
    hasDetailedInstructions: true,
  },
  equitiCorp: {
    id: "equitiCorp",
    name: "Equiti",
    minDeposit: 20000, // For companies
    userTypes: ["company"],
    logo: equitiLogo,
    logoWidth: 100,
    url: "https://portal.equiticlients.com/sc/corporate?clickid=501465&affid=C01620815",
    buttonStyle: { border: "1px solid #00afab", color: "#00afab", backgroundColor: "white" },
    hasDetailedInstructions: true,
  },
  vantage: {
    id: "vantage",
    name: "Vantage",
    minDeposit: 20000, // Same for both individual and company
    userTypes: ["individual", "company"],
    logo: vantageLogo,
    logoWidth: 110,
    url: "https://www.vantagemarkets.com/es/open-live-account/",
    buttonStyle: { border: "1px solid #034854", color: "#034854", backgroundColor: "white" },
    hasDetailedInstructions: false,
  },
  fbs: {
    id: "fbs",
    name: "FBS",
    minDeposit: 20000, // For individuals
    userTypes: ["individual"],
    logo: fbsLogo,
    logoWidth: 45,
    url: "https://fbs.eu/cabinet/registration/trader",
    buttonStyle: { border: "1px solid #00be40", color: "#00be40", backgroundColor: "white" },
    hasDetailedInstructions: false,
  },
  pepperstone: {
    id: "pepperstone",
    name: "Pepperstone",
    minDeposit: 20000, // For individuals
    userTypes: ["individual"],
    logo: pepperstoneLogo,
    logoWidth: 45,
    url: "https://secure.pepperstone.com/register/form",
    buttonStyle: { border: "1px solid #0064FA", color: "#0064FA", backgroundColor: "white" },
    hasDetailedInstructions: false,
  },
  squared: {
    id: "squared",
    name: "Squared Financial",
    minDeposit: 20000, // For companies
    userTypes: ["company"],
    logo: squaredLogo,
    logoWidth: 40,
    url: "https://www.sqfin.com/sc-ib/register/?lid=3853&pid=84962",
    buttonStyle: { border: "1px solid #c40233", backgroundColor: "white" },
    hasDetailedInstructions: false,
  },
};

// Helper to get minDeposit for a broker based on userType
const getBrokerMinDeposit = (broker: Broker, userType: string) => {
  if (typeof broker.minDeposit === "object") {
    return broker.minDeposit[userType] || 0;
  }
  return broker.minDeposit;
};

// Broker display order per userType and capitalTier
// Brokers not listed will appear at the end in their default order
const BROKER_ORDER = {
  individual: {
    "2k-20k": ["xm"],
    over20k: ["xm", "equiti", "vantage", "fbs", "pepperstone"],
  },
  company: {
    over20k: ["equitiCorp", "vantage", "squared"],
  },
};

// Get filtered brokers based on selections
const getFilteredBrokers = (userType: UserType, capitalTier: CapitalTier) => {
  // No brokers available for these tiers
  if (capitalTier === "under2k" || capitalTier === "under20k") return [];

  // Determine max deposit based on tier and userType
  // For 2k-20k tier, user has LESS than 20k, so only brokers with minDeposit < 20k qualify
  let maxDeposit = Infinity;
  if (userType === "individual" && capitalTier === "2k-20k") {
    maxDeposit = 19999; // Only XM qualifies (2k min), excludes 20k minimum brokers
  }
  // For over20k (individual or company), all brokers qualify

  const filteredBrokers = Object.values(BROKERS).filter((broker) => {
    const matchesUserType = broker.userTypes.includes(userType);
    const brokerMinDeposit = getBrokerMinDeposit(broker, userType);
    const matchesCapital = brokerMinDeposit <= maxDeposit;
    return matchesUserType && matchesCapital;
  });

  // Sort brokers based on configured order
  const getOrderConfig = (): string[] => {
    if (!userType || !capitalTier) return [];
    if (userType === "individual") {
      if (capitalTier === "2k-20k") return BROKER_ORDER.individual["2k-20k"];
      if (capitalTier === "over20k") return BROKER_ORDER.individual.over20k;
      return [];
    }
    if (userType === "company") {
      if (capitalTier === "over20k") return BROKER_ORDER.company.over20k;
      return [];
    }
    return [];
  };
  const orderConfig = getOrderConfig();
  return filteredBrokers.sort((a, b) => {
    const indexA = orderConfig.indexOf(a.id);
    const indexB = orderConfig.indexOf(b.id);
    // If not in order config, put at end
    const sortA = indexA === -1 ? 999 : indexA;
    const sortB = indexB === -1 ? 999 : indexB;
    return sortA - sortB;
  });
};

const TOTAL_STEPS = 4;

interface ClientOnboardingModalProps {
  isOpen: boolean;
  close: () => void;
  autoOpen?: boolean;
}

const ClientOnboardingModal = ({ isOpen, close }: ClientOnboardingModalProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation("modals", { keyPrefix: "onboarding" });

  const [step, setStep] = useState(0);
  const [userType, setUserType] = useState<UserType | null>(null);
  const [capitalTier, setCapitalTier] = useState<CapitalTier | null>(null);
  const [selectedBroker, setSelectedBroker] = useState<Broker | null>(null);
  const [brokerLinkClicked, setBrokerLinkClicked] = useState(false);

  const filteredBrokers = useMemo(() => {
    if (!userType || !capitalTier) return [];
    return getFilteredBrokers(userType, capitalTier);
  }, [userType, capitalTier]);

  const handleUserTypeSelect = (type: UserType) => {
    setUserType(type);
    setStep(1);
  };

  const handleCapitalSelect = (tier: CapitalTier) => {
    setCapitalTier(tier);
    setStep(2);
  };

  const handleBrokerSelect = (broker: Broker) => {
    setSelectedBroker(broker);
    setStep(3);
  };

  const handleBack = () => {
    if (step === 1) {
      setUserType(null);
    } else if (step === 2) {
      setCapitalTier(null);
    } else if (step === 3) {
      setSelectedBroker(null);
      setBrokerLinkClicked(false);
    }
    setStep((s) => s - 1);
  };

  const handleAlreadyHaveBroker = () => {
    close();
    navigate("/exchange-account");
  };

  const handleOpenBroker = () => {
    setBrokerLinkClicked(true);
  };

  const getButtons = () => {
    if (step === 0) return undefined;

    return [
      {
        children: t("prevStepBtn"),
        onClick: handleBack,
        color: "secondary",
        outline: true,
        size: "sm",
      },
    ];
  };

  return (
    <Modal.Simple title={t("title")} isOpen={isOpen} buttons={getButtons()} toggleOpen={close} size="md">
      <div>
        {/* Progress indicator with help icon */}
        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <small className="text-muted" style={{ fontSize: "0.75rem" }}>
              {t("stepIndicator", { current: step + 1, total: TOTAL_STEPS })}
            </small>
            <div className="d-flex align-items-center">
              <small className="text-muted mr-2" style={{ fontSize: "0.75rem" }}>
                {Math.round(((step + 1) / TOTAL_STEPS) * 100)}%
              </small>
              <i
                id="onboarding-help-icon"
                className="fas fa-question-circle text-muted"
                style={{ fontSize: "1rem", cursor: "help", padding: "4px" }}
              />
              <UncontrolledTooltip placement="left" target="onboarding-help-icon">
                {t("intro")}
              </UncontrolledTooltip>
            </div>
          </div>
          <Progress value={((step + 1) / TOTAL_STEPS) * 100} style={{ height: "4px" }} />
        </div>

        {/* Step content */}
        {step === 0 && (
          <UserTypeStep t={t} onSelect={handleUserTypeSelect} onAlreadyHaveBroker={handleAlreadyHaveBroker} />
        )}
        {step === 1 && <CapitalStep t={t} userType={userType} onSelect={handleCapitalSelect} />}
        {step === 2 && (
          <BrokerResultsStep
            t={t}
            brokers={filteredBrokers}
            capitalTier={capitalTier}
            userType={userType}
            onSelectBroker={handleBrokerSelect}
            onGoBack={handleBack}
          />
        )}
        {step === 3 && selectedBroker && (
          <BrokerDetailsStep
            t={t}
            broker={selectedBroker}
            capitalTier={capitalTier}
            onAccountReady={handleAlreadyHaveBroker}
            onOpenBroker={handleOpenBroker}
            brokerLinkClicked={brokerLinkClicked}
          />
        )}
      </div>
    </Modal.Simple>
  );
};

// Step 1: User Type Selection
const UserTypeStep = ({
  t,
  onSelect,
  onAlreadyHaveBroker,
}: {
  t: TFunction;
  onSelect: (type: UserType) => void;
  onAlreadyHaveBroker: () => void;
}) => {
  const [isAlreadyHaveHovered, setIsAlreadyHaveHovered] = useState(false);
  return (
    <div>
      <p className="text-muted small mb-2">{t("steps.userType.explanation")}</p>
      <h5 className="mb-3">{t("steps.userType.question")}</h5>

      <Row className="mb-3">
        <Col xs="6">
          <Button
            outline
            color="default"
            className="w-100 py-3 px-2 h-100 d-flex flex-column align-items-center justify-content-center"
            style={{ minHeight: 80, borderRadius: "8px" }}
            onClick={() => onSelect("individual")}
          >
            <i className="fas fa-user fa-lg mb-2" style={{ color: "#5e72e4" }} />
            <span className="font-weight-bold small text-center">{t("steps.userType.individual")}</span>
          </Button>
        </Col>
        <Col xs="6">
          <Button
            outline
            color="default"
            className="w-100 py-3 px-2 h-100 d-flex flex-column align-items-center justify-content-center"
            style={{ minHeight: 80, borderRadius: "8px" }}
            onClick={() => onSelect("company")}
          >
            <i className="fas fa-building fa-lg mb-2" style={{ color: "#5e72e4" }} />
            <span className="font-weight-bold small text-center">{t("steps.userType.company")}</span>
          </Button>
        </Col>
      </Row>

      {/* Divider */}
      <div className="d-flex align-items-center my-4">
        <hr className="flex-grow-1 m-0" style={{ borderColor: "#e9ecef" }} />
        <span className="px-3 text-muted small text-uppercase" style={{ fontSize: "0.7rem", letterSpacing: "0.5px" }}>
          {t("steps.userType.orDivider", "or")}
        </span>
        <hr className="flex-grow-1 m-0" style={{ borderColor: "#e9ecef" }} />
      </div>

      {/* Already have broker button */}
      <Row>
        <Col xs="12">
          <Button
            outline={isAlreadyHaveHovered}
            color="success"
            className="w-100"
            size="sm"
            style={{ border: "1px solid #2dce89", transition: "background-color 0.15s, color 0.15s" }}
            onMouseEnter={() => setIsAlreadyHaveHovered(true)}
            onMouseLeave={() => setIsAlreadyHaveHovered(false)}
            onClick={onAlreadyHaveBroker}
          >
            <i className="fas fa-check-circle mr-2" />
            {t("steps.userType.alreadyHaveBroker")}
          </Button>
        </Col>
      </Row>
    </div>
  );
};

// Step 2: Capital Selection
const CapitalStep = ({
  t,
  userType,
  onSelect,
}: {
  t: TFunction;
  userType: UserType | null;
  onSelect: (tier: CapitalTier) => void;
}) => {
  // Companies have different options - only under20k or over20k
  if (userType === "company") {
    return (
      <div>
        <p className="text-muted small mb-2">{t("steps.capital.explanationCompany")}</p>
        <h5 className="mb-3">{t("steps.capital.question")}</h5>

        <Row className="justify-content-center">
          <Col xs="12" sm="6" className="mb-2 mb-sm-0">
            <Button
              outline
              color="warning"
              className="w-100 py-3 px-2 d-flex flex-column align-items-center justify-content-center"
              style={{ minHeight: 70, borderRadius: "8px" }}
              onClick={() => onSelect("under20k")}
            >
              <i className="fas fa-exclamation-triangle mb-1" style={{ color: "#d97706" }} />
              <span className="font-weight-bold small text-dark">{t("steps.capital.under20k")}</span>
            </Button>
          </Col>
          <Col xs="12" sm="6">
            <Button
              outline
              color="success"
              className="w-100 py-3 px-2 d-flex flex-column align-items-center justify-content-center"
              style={{ minHeight: 70, borderRadius: "8px" }}
              onClick={() => onSelect("over20k")}
            >
              <i className="fas fa-gem mb-1" style={{ color: "#059669" }} />
              <span className="font-weight-bold small text-dark">{t("steps.capital.over20k")}</span>
            </Button>
          </Col>
        </Row>
      </div>
    );
  }

  // Individuals have 3 options
  return (
    <div>
      <p className="text-muted small mb-2">{t("steps.capital.explanation")}</p>
      <h5 className="mb-3">{t("steps.capital.question")}</h5>

      <Row>
        <Col xs="12" sm="4" className="mb-2 mb-sm-0">
          <Button
            outline
            color="warning"
            className="w-100 py-3 px-2 d-flex flex-column align-items-center justify-content-center"
            style={{ minHeight: 70, borderRadius: "8px" }}
            onClick={() => onSelect("under2k")}
          >
            <i className="fas fa-exclamation-triangle mb-1" style={{ color: "#d97706" }} />
            <span className="font-weight-bold small text-dark">{t("steps.capital.under2k")}</span>
          </Button>
        </Col>
        <Col xs="12" sm="4" className="mb-2 mb-sm-0">
          <Button
            outline
            color="default"
            className="w-100 py-3 px-2 d-flex flex-column align-items-center justify-content-center"
            style={{ minHeight: 70, borderRadius: "8px" }}
            onClick={() => onSelect("2k-20k")}
          >
            <i className="fas fa-coins mb-1" style={{ color: "#5e72e4" }} />
            <span className="font-weight-bold small">{t("steps.capital.2k-20k")}</span>
          </Button>
        </Col>
        <Col xs="12" sm="4">
          <Button
            outline
            color="success"
            className="w-100 py-3 px-2 d-flex flex-column align-items-center justify-content-center"
            style={{ minHeight: 70, borderRadius: "8px" }}
            onClick={() => onSelect("over20k")}
          >
            <i className="fas fa-gem mb-1" style={{ color: "#059669" }} />
            <span className="font-weight-bold small text-dark">{t("steps.capital.over20k")}</span>
          </Button>
        </Col>
      </Row>
    </div>
  );
};

// Step 3: Broker Results
const BrokerResultsStep = ({
  t,
  brokers,
  capitalTier,
  userType,
  onSelectBroker,
  onGoBack,
}: {
  t: TFunction;
  brokers: Broker[];
  capitalTier: CapitalTier | null;
  userType: UserType | null;
  onSelectBroker: (broker: Broker) => void;
  onGoBack: () => void;
}) => {
  // Individual with under €2k
  if (capitalTier === "under2k") {
    return (
      <div className="text-center py-3">
        <i className="fas fa-exclamation-circle fa-2x mb-2" style={{ color: "#d97706" }} />
        <h5>{t("steps.brokers.warningTitle")}</h5>
        <p className="text-muted small mb-3">{t("steps.capital.warning")}</p>
        <Button outline color="default" size="sm" onClick={onGoBack}>
          {t("steps.brokers.goBack")}
        </Button>
      </div>
    );
  }

  // Company with under €20k
  if (capitalTier === "under20k") {
    return (
      <div className="text-center py-3">
        <i className="fas fa-building fa-2x mb-2" style={{ color: "#d97706" }} />
        <h5>{t("steps.brokers.companyMinimumTitle")}</h5>
        <p className="text-muted small mb-3">{t("steps.capital.warningCompany")}</p>
        <Button outline color="default" size="sm" onClick={onGoBack}>
          {t("steps.brokers.goBack")}
        </Button>
      </div>
    );
  }

  const isSingleBroker = brokers.length === 1;

  return (
    <div>
      <h5 className="mb-1">{t("steps.brokers.title")}</h5>
      <p className="text-muted small mb-3">{t("steps.brokers.subtitle")}</p>

      <Row className={isSingleBroker ? "justify-content-center" : ""}>
        {brokers.map((broker: Broker) => (
          <Col xs={isSingleBroker ? "8" : "6"} sm={isSingleBroker ? "6" : "6"} className="mb-2" key={broker.id}>
            <BrokerCard broker={broker} userType={userType} t={t} onSelect={() => onSelectBroker(broker)} />
          </Col>
        ))}
      </Row>
    </div>
  );
};

// Broker Card (clickable, advances to step 4)
const BrokerCard = ({
  broker,
  userType,
  t,
  onSelect,
}: {
  broker: Broker;
  userType: UserType | null;
  t: TFunction;
  onSelect: () => void;
}) => {
  const { i18n } = useTranslation();
  const minDeposit = getBrokerMinDeposit(broker, userType || "individual");
  return (
    <div>
      <Button
        className="w-100 d-flex align-items-center justify-content-center"
        style={{ minHeight: 50, borderRadius: "6px", ...broker.buttonStyle }}
        onClick={onSelect}
      >
        <img
          src={broker.logo}
          style={{ maxWidth: broker.logoWidth * 0.85, width: "100%", height: "auto" }}
          alt={broker.name}
        />
      </Button>
      <small className="text-muted d-block text-center" style={{ fontSize: "0.7rem" }}>
        {t("minDeposit")}:{" "}
        {new Intl.NumberFormat(i18n.language, {
          style: "currency",
          currency: "EUR",
          maximumFractionDigits: 0,
        }).format(minDeposit)}
      </small>
    </div>
  );
};

// Step 4: Broker Details & Instructions
const BrokerDetailsStep = ({
  t,
  broker,
  capitalTier,
  onAccountReady,
  onOpenBroker,
  brokerLinkClicked,
}: {
  t: TFunction;
  broker: Broker;
  capitalTier: CapitalTier | null;
  onAccountReady: () => void;
  onOpenBroker: () => void;
  brokerLinkClicked: boolean;
}) => {
  // Get broker-specific instructions
  const renderBrokerInstructions = () => {
    // XM has detailed instructions
    if (broker.id === "xm") {
      const accountType = capitalTier === "2k-20k" ? "Micro Ultralow" : "Standard Ultralow";
      return (
        <ul className="list-unstyled small mb-0">
          <li className="mb-1">
            <i className="fas fa-check mr-2" style={{ color: "#059669" }} />
            {t("steps.brokerDetails.instructions.xm.accountTypeLabel")}: <strong>{accountType}</strong>
          </li>
          <li className="mb-1">
            <i className="fas fa-check mr-2" style={{ color: "#059669" }} />
            {t("steps.brokerDetails.instructions.xm.leverageLabel")}: 1:1000
          </li>
          <li className="mb-1">
            <i className="fas fa-check mr-2" style={{ color: "#059669" }} />
            {t("steps.brokerDetails.instructions.xm.currencyLabel")}: EUR
          </li>
        </ul>
      );
    }

    // Equiti has RAW account instruction
    if (broker.id === "equiti" || broker.id === "equitiCorp") {
      return (
        <ul className="list-unstyled small mb-0">
          <li className="mb-1">
            <i className="fas fa-check mr-2" style={{ color: "#059669" }} />
            {t("steps.brokerDetails.instructions.equiti.accountTypeLabel")}: <strong>RAW</strong>
          </li>
        </ul>
      );
    }

    // Default for other brokers
    return <p className="text-muted small mb-0">{t("steps.brokerDetails.instructions.default.followSteps")}</p>;
  };

  return (
    <div>
      {/* Broker header */}
      <div className="text-center mb-3">
        <div
          className="d-inline-flex align-items-center justify-content-center py-2 px-3 rounded mb-2"
          style={{ ...broker.buttonStyle, borderRadius: "6px", maxWidth: "80%" }}
        >
          <img
            src={broker.logo}
            style={{ maxWidth: broker.logoWidth * 0.8, width: "100%", height: "auto" }}
            alt={broker.name}
          />
        </div>
        <h6 className="mb-0">{t("steps.brokerDetails.title", { broker: broker.name })}</h6>
      </div>

      {/* Credit/Bonus Warning - Universal for all brokers */}
      <Alert color="warning" className="mb-3 py-2 px-3" style={{ backgroundColor: "#fef3c7", borderColor: "#f59e0b" }}>
        <div className="d-flex align-items-start">
          <i className="fas fa-exclamation-triangle mr-2 mt-1" style={{ fontSize: "0.85rem", color: "#d97706" }} />
          <div>
            <strong className="small" style={{ color: "#92400e" }}>
              {t("steps.brokerDetails.creditWarningTitle")}
            </strong>
            <p className="mb-0 small" style={{ color: "#78350f" }}>
              {t("steps.brokerDetails.creditWarning")}
            </p>
          </div>
        </div>
      </Alert>

      {/* Broker-specific instructions */}
      <div>
        <h6 className="mb-2">{t("steps.brokerDetails.instructionsTitle")}</h6>
        {renderBrokerInstructions()}
      </div>

      {/* Action buttons */}
      <Row className="mt-4">
        <Col xs="12" sm="6" className="mb-2 mb-sm-0">
          <Button
            tag="a"
            href={broker.url}
            target="_blank"
            rel="noopener noreferrer"
            outline
            color="default"
            className="w-100"
            size="sm"
            onClick={onOpenBroker}
          >
            <i className="fas fa-external-link-alt mr-2" aria-hidden="true" />
            {t("openBrokerBtn")}
          </Button>
        </Col>
        <Col xs="12" sm="6">
          <span id="account-ready-btn-wrapper" className="d-block">
            <Button
              color="primary"
              className="w-100"
              size="sm"
              onClick={onAccountReady}
              disabled={!brokerLinkClicked}
              style={!brokerLinkClicked ? { pointerEvents: "none" } : {}}
            >
              <i className="fas fa-check mr-2" />
              {t("steps.brokerDetails.accountReady")}
            </Button>
          </span>
          {!brokerLinkClicked && (
            <UncontrolledTooltip placement="top" target="account-ready-btn-wrapper">
              {t("steps.brokerDetails.accountReadyDisabledTooltip")}
            </UncontrolledTooltip>
          )}
          {!brokerLinkClicked && (
            <Button
              color="link"
              size="sm"
              className="w-100 mt-1 p-0"
              onClick={onAccountReady}
            >
              {t("steps.brokerDetails.alreadyHaveAccount")}
            </Button>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default ClientOnboardingModal;
